import { windowEndOf } from './leadRounds';
import type { WindowUnit } from './leadRounds';
import { isTouchKind } from './leadActivity';
import type { LeadActivity } from './leadActivity';

/**
 * What a round about to start would claim from what has already happened.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Kyu asked for this the week of 24 Aug and it was misread twice — first as
 * "enrol them in round two", then as a request to fabricate a round that never
 * ran. It is neither. Jacob Davis has four emails logged by Hanneza on 25 and
 * 27 August. Starting a round on him used to give him 0 of 7, so the team
 * would send seven more and he would receive eleven.
 *
 * The rule, in one line: A ROUND HAS A START DATE, AND EVERY EMAIL ALREADY
 * LOGGED ON OR AFTER THAT DATE JOINS IT.
 *
 * ── This file does not decide anything ─────────────────────────────────────
 *
 * `start_lead_round` does the claiming, in the database, in one UPDATE. This
 * module exists so the drawer can SHOW what that UPDATE will pick up before
 * anybody presses the button — the same division as `leadRoundEnrol.ts` and
 * `start_rounds_for_leads`. The two must agree exactly, which is why the day
 * is read in UTC here: the database compares
 * `(occurred_at at time zone 'UTC')::date`, and a browser in Florida reading
 * local days would disagree with it for five hours out of every twenty-four
 * and show a preview that is quietly wrong at 8pm.
 */

/** The day an activity happened, as the database reads it. */
export function activityDay(occurredAt: string): string {
  return new Date(occurredAt).toISOString().slice(0, 10);
}

export interface AdoptionShape {
  startsOn: string;
  windowLength: number;
  windowUnit: WindowUnit;
}

/**
 * The activities a round of this shape would claim, oldest first.
 *
 * Three tests, and each one matches a line of the migration:
 *
 *   unclaimed   `round_id is null`. An email that belonged to round 1 is never
 *               moved into round 2, so nothing is counted twice and starting a
 *               round can never alter one that has already finished.
 *   outreach    touches and replies. A note is us talking to ourselves and is
 *               not part of a round.
 *   in range    on or after the start date, and not after the window closes. A
 *               round dated 1 Aug over 7 business days is over on 11 Aug; an
 *               email sent on the 20th is not part of it.
 */
export function adoptedBy(activity: readonly LeadActivity[], shape: AdoptionShape): LeadActivity[] {
  const windowEnd = windowEndOf(shape.startsOn, shape.windowLength, shape.windowUnit);
  return activity
    .filter((entry) => {
      if (entry.round_id !== null) {
        return false;
      }
      if (!isTouchKind(entry.kind) && entry.kind !== 'reply_received') {
        return false;
      }
      const day = activityDay(entry.occurred_at);
      return day >= shape.startsOn && day <= windowEnd;
    })
    .sort((a, b) => activityDay(a.occurred_at).localeCompare(activityDay(b.occurred_at)));
}

export interface AdoptionPreview {
  /** Everything the round would claim, oldest first. */
  claimed: LeadActivity[];
  /** Of those, the ones that count toward the target. */
  touches: number;
  /** Follow-ups still to send if the round starts now. */
  left: number;
  /**
   * True if the round would end the moment it started.
   *
   * Either the claimed history already meets the target, or one of the claimed
   * rows is a reply. Both are real endings the database applies on the spot,
   * and a button that says "Start round 1" while quietly starting and closing
   * one in the same breath is the kind of surprise this whole panel exists to
   * prevent.
   */
  endsImmediately: boolean;
  /** Which of the two, so the drawer can say which. */
  endsBecause: 'replied' | 'count_met' | null;
}

export function previewAdoption(
  activity: readonly LeadActivity[],
  shape: AdoptionShape,
  touchTarget: number,
): AdoptionPreview {
  const claimed = adoptedBy(activity, shape);
  const touches = claimed.filter((entry) => isTouchKind(entry.kind)).length;
  // A reply outranks a count, the same order the database checks them in: it
  // ends the round early and well, and it is the one ending that means the
  // opposite of the other two.
  const replied = claimed.some((entry) => entry.kind === 'reply_received');
  const met = touches >= touchTarget;
  return {
    claimed,
    touches,
    left: Math.max(0, touchTarget - touches),
    endsImmediately: replied || met,
    endsBecause: replied ? 'replied' : met ? 'count_met' : null,
  };
}

/**
 * How far back the drawer looks for unclaimed work when it opens.
 *
 * Kyu's choice, 31 Aug, from two weeks / thirty days / never: thirty days.
 * Older than that and a fresh round starting today is the honest answer —
 * a round back-dated six weeks opens already out of time, which reads as a
 * failure nobody had a chance to prevent.
 */
export const ADOPTION_LOOK_BACK_DAYS = 30;

/**
 * The date the drawer should open on.
 *
 * The day of the oldest unclaimed touch within the look-back, or today if
 * there is none. Only touches move it: a lone reply or a note is not work the
 * round should be back-dated to cover.
 *
 * It is a DEFAULT and nothing more. The date stays editable, and moving it to
 * today empties the preview — which is the whole reason the rule is one
 * sentence about a date rather than a tick box somebody has to remember.
 */
export function defaultStartsOn(
  activity: readonly LeadActivity[],
  today: string,
  lookBackDays: number = ADOPTION_LOOK_BACK_DAYS,
): string {
  const floor = new Date(`${today}T00:00:00Z`);
  floor.setUTCDate(floor.getUTCDate() - lookBackDays);
  const earliest = floor.toISOString().slice(0, 10);

  let found: string | null = null;
  for (const entry of activity) {
    if (entry.round_id !== null || !isTouchKind(entry.kind)) {
      continue;
    }
    const day = activityDay(entry.occurred_at);
    // Not before the look-back, and not in the future — a touch dated ahead of
    // today would drag the start date forward and hide the history behind it.
    if (day < earliest || day > today) {
      continue;
    }
    if (found === null || day < found) {
      found = day;
    }
  }
  return found ?? today;
}
